import { useState } from "react";
import { useQuery } from "react-query";
import {
  type AiWorkflowsBillingHistoryParams,
  aiWorkflowsBillingHistoryApi,
} from "@/react/api/aiWorkflowsBillingHistoryApi";

export interface UseAiWorkflowsBillingHistoryProps {
  enabled?: boolean;
  end_date: string;
  free_quantity: number;
  initialPage?: number;
  initialPerPage?: number;
  initialSortDirection?: "asc" | "desc";
  initialSortField?: string;
  price_overage: number;
  start_date: string;
}

const DEFAULT_PER_PAGE = 10;
const DEFAULT_SORT_FIELD = "total_credits_consumed";
const DEFAULT_SORT_DIRECTION = "desc";

export const useAiWorkflowsBillingHistory = ({
  start_date,
  end_date,
  free_quantity,
  price_overage,
  initialPage = 1,
  initialPerPage = DEFAULT_PER_PAGE,
  initialSortField = DEFAULT_SORT_FIELD,
  initialSortDirection = DEFAULT_SORT_DIRECTION,
  enabled = true,
}: UseAiWorkflowsBillingHistoryProps) => {
  const [page, setPage] = useState(initialPage);
  const [perPage, setPerPage] = useState(initialPerPage);
  const [sortField, setSortField] = useState(initialSortField);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    initialSortDirection,
  );

  const params: AiWorkflowsBillingHistoryParams = {
    start_date,
    end_date,
    free_quantity,
    price_overage,
    page,
    per_page: perPage,
    sort_field: sortField,
    sort_direction: sortDirection,
  };

  const { data, isLoading, error, refetch } = useQuery(
    ["ai-workflows-billing-history", params],
    () => aiWorkflowsBillingHistoryApi.fetchBillingHistoryData(params),
    {
      enabled: enabled && !!start_date && !!end_date,
      keepPreviousData: true,
    },
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1); // Reset to first page when changing items per page
  };

  const handleSortChange = (field: string) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setPage(1); // Reset to first page when changing sort
  };

  return {
    workflows: data?.workflows || [],
    pagination: data?.pagination || {
      current_page: page,
      per_page: perPage,
      total_pages: 0,
      total_count: 0,
    },
    isLoading,
    error,
    refetch,
    page,
    perPage,
    sortField,
    sortDirection,
    handlePageChange,
    handlePerPageChange,
    handleSortChange,
  };
};
